import { Link } from 'wouter'
import { LegalLayout, LegalSection } from './LegalLayout'

export function Privacidad() {
  return (
    <LegalLayout title="Política de Privacidad" lastUpdated="5 de junio de 2026">
      <p className="text-muted-foreground">
        Esta Política de Privacidad describe cómo <strong>PFP App Service LLC</strong> recopila, usa,
        comparte y protege los datos personales tratados a través de la plataforma{' '}
        <strong>Facil Control</strong> y su aplicación móvil. Te recomendamos leerla junto con
        nuestros{' '}
        <Link href="/terminos" className="text-primary hover:underline">
          Términos y Condiciones
        </Link>.
      </p>

      <LegalSection title="1. Responsable del tratamiento">
        <p>
          El responsable es <strong>PFP App Service LLC</strong>, RUT <strong>76.075.598-2</strong>,
          con domicilio en Presidente Riesco 5711, 7561114, comuna de Las Condes, Región
          Metropolitana, Chile. Para ejercer tus derechos o realizar consultas sobre privacidad,
          escríbenos a{' '}
          <a href="mailto:contacto@facilcontrol.cl" className="text-primary hover:underline">
            contacto@facilcontrol.cl
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="2. Marco legal">
        <p>
          Tratamos los datos personales conforme a la Ley N° 19.628 sobre Protección de la Vida
          Privada, actualmente vigente, y en alineación con la Ley N° 21.719 sobre Protección de
          Datos Personales, que entra en vigencia el 1 de diciembre de 2026. Asimismo, el servicio
          opera en el contexto de la normativa de seguridad y salud en el trabajo (Ley N° 16.744 y
          Decreto Supremo N° 76), que puede exigir la conservación de determinados registros como
          evidencia de cumplimiento.
        </p>
      </LegalSection>

      <LegalSection title="3. Roles: responsable y encargado">
        <p>
          Es importante distinguir dos situaciones:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Datos de cuenta y facturación:</strong> respecto de los datos de los Usuarios que
            crean una cuenta y de los datos comerciales, PFP App Service actúa como{' '}
            <strong>responsable</strong> del tratamiento.
          </li>
          <li>
            <strong>Datos de los Trabajadores cargados por la Empresa:</strong> respecto de los datos
            de los Trabajadores y de los documentos de seguridad que cada Empresa cliente incorpora,
            la <strong>Empresa</strong> es el responsable del tratamiento y PFP App Service actúa como{' '}
            <strong>encargado</strong> (mandatario), tratando esos datos por cuenta y según las
            instrucciones de la Empresa. La Empresa es responsable de contar con base de licitud para
            tratar dichos datos y de informar a sus Trabajadores.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Datos personales que recopilamos">
        <p>Según el uso del servicio, podemos tratar las siguientes categorías de datos:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Datos identificatorios:</strong> nombre, apellido, RUT, correo electrónico y
            teléfono de Usuarios y Trabajadores.
          </li>
          <li>
            <strong>Datos de cuenta y autenticación:</strong> credenciales de acceso (la contraseña
            se almacena cifrada mediante algoritmos de hash; nunca en texto plano), tokens de sesión,
            dirección IP y agente de usuario del navegador o dispositivo.
          </li>
          <li>
            <strong>Datos laborales:</strong> cargo, empresa, contratos, áreas y estado de actividad
            del Trabajador.
          </li>
          <li>
            <strong>Datos de empresa:</strong> razón social, nombre de fantasía, RUT, giro,
            dirección, correo y teléfono de contacto.
          </li>
          <li>
            <strong>Datos de seguridad ocupacional:</strong> registros de capacitaciones, entrega de
            elementos de protección personal (EPP), inspecciones de vehículos y maquinaria, y
            documentos de análisis de trabajo seguro (ATS).
          </li>
          <li>
            <strong>Firmas digitales:</strong> el trazo manuscrito digitalizado que el Trabajador
            registra como evidencia de recepción, asistencia o conformidad.
          </li>
          <li>
            <strong>Datos de geolocalización:</strong> en ciertos registros de firma, las coordenadas
            del momento en que se realiza la acción, como evidencia de cumplimiento.
          </li>
          <li>
            <strong>Datos de dispositivo:</strong> identificadores del dispositivo asociados a la
            captura de firmas y a la sincronización de la Aplicación.
          </li>
          <li>
            <strong>Respuestas a evaluaciones:</strong> en el módulo de Capacitaciones con IA, las
            respuestas del Trabajador a los cuestionarios y las marcas de tiempo de apertura y
            respuesta.
          </li>
          <li>
            <strong>Datos de facturación:</strong> número de factura, montos, método y fecha de pago.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Datos sensibles y de especial cuidado">
        <p>
          Algunos datos requieren un cuidado especial: las <strong>firmas digitales</strong> (que
          pueden tener naturaleza biométrico-conductual) y los <strong>datos de geolocalización</strong>{' '}
          asociados a acciones laborales. Estos datos se tratan exclusivamente para constituir
          evidencia de cumplimiento de la normativa de seguridad y salud en el trabajo, y se protegen
          con medidas de seguridad reforzadas.
        </p>
      </LegalSection>

      <LegalSection title="6. Finalidades del tratamiento">
        <ul className="list-disc space-y-2 pl-5">
          <li>Prestar, operar y mantener la Plataforma y la Aplicación.</li>
          <li>Autenticar Usuarios y gestionar el control de acceso.</li>
          <li>
            Gestionar la documentación de seguridad laboral y generar evidencia de cumplimiento
            normativo (DS 76, Ley N° 16.744).
          </li>
          <li>Generar y distribuir capacitaciones y cuestionarios mediante el módulo de IA.</li>
          <li>Procesar pagos y emitir la facturación correspondiente.</li>
          <li>Enviar comunicaciones transaccionales (invitaciones, recuperación de contraseña, avisos).</li>
          <li>Cumplir obligaciones legales y atender requerimientos de autoridad competente.</li>
        </ul>
      </LegalSection>

      <LegalSection title="7. Capacitaciones con IA y decisiones automatizadas">
        <p>
          El módulo de Capacitaciones con IA genera contenido y cuestionarios de forma automatizada.
          Para ello, se procesan datos de la empresa, los documentos laborales que la Empresa decida
          cargar (por ejemplo, reglamento interno o matriz de riesgo) y el nombre y correo de los
          Trabajadores destinatarios. El procesamiento se realiza en la infraestructura de PFP App
          Service, que a su vez utiliza el motor de inteligencia artificial de Google (Gemini) como
          subencargado. Los contenidos generados son de apoyo y deben ser revisados por la Empresa
          antes de su uso. La corrección de los cuestionarios no produce efectos jurídicos
          automáticos para el Trabajador sin la intervención de la Empresa.
        </p>
      </LegalSection>

      <LegalSection title="8. Encargados y terceros con quienes compartimos datos">
        <p>
          No vendemos datos personales. Para operar el servicio, recurrimos a proveedores que actúan
          como encargados o subencargados, comprometidos a tratar los datos solo según nuestras
          instrucciones:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Twilio SendGrid:</strong> envío de correos electrónicos transaccionales. Recibe
            el nombre y el correo del destinatario.
          </li>
          <li>
            <strong>Google LLC (Gemini):</strong> motor de inteligencia artificial utilizado por el
            módulo de Capacitaciones con IA. Procesa los datos descritos en la sección 7.
          </li>
          <li>
            <strong>Amazon Web Services (AWS):</strong> infraestructura de almacenamiento de archivos
            y documentos, cuando se encuentra habilitada.
          </li>
        </ul>
        <p>
          También podremos compartir datos cuando lo exija la ley o una autoridad competente, o para
          proteger derechos legítimos conforme a la normativa aplicable.
        </p>
      </LegalSection>

      <LegalSection title="9. Transferencias internacionales">
        <p>
          Algunos de nuestros proveedores (como Twilio SendGrid, Google y Amazon Web Services) tratan
          datos en servidores ubicados fuera de Chile, principalmente en Estados Unidos. En estos
          casos adoptamos resguardos contractuales y técnicos razonables para proteger los datos
          conforme a la legislación chilena aplicable.
        </p>
      </LegalSection>

      <LegalSection title="10. Conservación de los datos">
        <p>
          Conservamos los datos personales mientras la cuenta esté activa y exista una relación con la
          Empresa. Los registros que constituyen evidencia de cumplimiento de seguridad laboral
          (firmas, capacitaciones, inspecciones, entregas de EPP) tienen carácter de evidencia y se
          conservan por los plazos que exija la normativa de seguridad y salud en el trabajo, aun
          después del término de la relación. Cumplidos los plazos legales, los datos se eliminan o
          anonimizan de forma segura.
        </p>
      </LegalSection>

      <LegalSection title="11. Seguridad de la información">
        <p>
          Aplicamos medidas técnicas y organizativas razonables para proteger los datos, incluyendo el
          cifrado de contraseñas mediante funciones de hash, el almacenamiento de tokens de acceso en
          forma cifrada (no reversible), control de acceso por roles y registros de actividad. Ninguna
          medida de seguridad es infalible, por lo que no podemos garantizar una seguridad absoluta,
          pero trabajamos continuamente para reducir los riesgos.
        </p>
      </LegalSection>

      <LegalSection title="12. Derechos de los titulares">
        <p>
          Como titular de datos personales, puedes ejercer tus derechos de acceso, rectificación,
          cancelación (supresión) y oposición. Con la entrada en vigencia de la Ley N° 21.719 se
          incorporan, además, derechos como la portabilidad de datos, la limitación del tratamiento y
          el derecho a no ser objeto de decisiones automatizadas sin intervención humana, en los
          términos que la ley establezca.
        </p>
        <p>
          Para ejercerlos, escríbenos a{' '}
          <a href="mailto:contacto@facilcontrol.cl" className="text-primary hover:underline">
            contacto@facilcontrol.cl
          </a>. Si los datos fueron incorporados por una Empresa en su calidad de responsable
          (por ejemplo, datos de un Trabajador), canalizaremos tu solicitud hacia dicha Empresa o te
          derivaremos a ella, según corresponda.
        </p>
      </LegalSection>

      <LegalSection title="13. Datos de menores de edad">
        <p>
          El servicio está dirigido a personas mayores de edad en un contexto laboral o comercial. No
          recopilamos deliberadamente datos de menores de edad. Si detectamos que se han incorporado
          datos de un menor sin la debida autorización, adoptaremos medidas para eliminarlos.
        </p>
      </LegalSection>

      <LegalSection title="14. Cookies y tecnologías similares">
        <p>
          Utilizamos almacenamiento técnico y tokens de sesión estrictamente necesarios para
          autenticar a los Usuarios y mantener la sesión activa. No utilizamos estas tecnologías con
          fines publicitarios.
        </p>
      </LegalSection>

      <LegalSection title="15. Cambios a esta Política">
        <p>
          Podremos actualizar esta Política para reflejar cambios legales o del servicio. Las
          modificaciones se publicarán en esta misma página, indicando la fecha de última
          actualización. Te recomendamos revisarla periódicamente.
        </p>
      </LegalSection>

      <LegalSection title="16. Contacto y reclamos">
        <p>
          Para consultas, solicitudes o reclamos sobre el tratamiento de tus datos personales,
          contáctanos en{' '}
          <a href="mailto:contacto@facilcontrol.cl" className="text-primary hover:underline">
            contacto@facilcontrol.cl
          </a>{' '}
          o por correo a Presidente Riesco 5711, 7561114, Las Condes, Región Metropolitana, Chile.
          Sin perjuicio de ello, podrás recurrir ante la autoridad de control competente en materia
          de protección de datos personales conforme a la legislación vigente.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
